import { call, type Schemas } from "@saas/api-client";
import { Alert, Badge, Button, Card, ConfirmAction, ErrorAlert, formatDate, Loading, PageHeader, Table, TextField, useAsync, useCommand, usePendingId } from "@saas/ui";
import { useState, type FormEvent } from "react";
import { api } from "./api";

export function RoomsPage() {
  const rooms = useAsync(() => call(api.GET("/api/rooms/all")), []);
  const command = useCommand();
  const roomId = usePendingId();
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("4");
  const [added, setAdded] = useState<string>();

  const add = async (event: FormEvent) => {
    event.preventDefault();
    setAdded(undefined);
    const body = { id: roomId.idFor(name, capacity), name, capacity: Number(capacity) };
    if (await command.run(() => call(api.POST("/api/rooms", { body })))) {
      roomId.done();
      setAdded(name);
      setName("");
      rooms.reload();
    }
  };

  const deactivate = async (id: string) => {
    if (await command.run(() => call(api.POST("/api/rooms/{id}/deactivate", { params: { path: { id } } })))) rooms.reload();
  };

  return (
    <>
      <PageHeader title="Rooms" description="The rooms your members can book. Deactivating a room is final: its upcoming bookings are cancelled and each booker gets an email." />
      <Card title="Add a room">
        <form className="sb-form" onSubmit={add} noValidate>
          <div className="sb-form-row">
            <TextField label="Room name" required maxLength={200} value={name} error={command.fieldError("name")} onChange={(e) => setName(e.target.value)} />
            <TextField label="Seats" type="number" min={1} max={1000} required value={capacity} error={command.fieldError("capacity")} onChange={(e) => setCapacity(e.target.value)} />
            <div className="sb-form-action">
              <Button type="submit" variant="primary" disabled={command.pending}>
                Add this room
              </Button>
            </div>
          </div>
          {added && <Alert tone="success">Room {added} added.</Alert>}
        </form>
      </Card>
      <Card title="All rooms">
        <ErrorAlert error={command.error && !command.fieldError("name") && !command.fieldError("capacity") ? command.error : rooms.error} />
        {rooms.loading && !rooms.data ? (
          <Loading />
        ) : rooms.data && rooms.data.length === 0 ? (
          <p className="sb-muted">No rooms yet.</p>
        ) : (
          <Table caption={`${rooms.data?.length ?? 0} room(s)`} headers={["Name", "Seats", "Status", "Added", ""]}>
            {rooms.data?.map((r) => (
              <RoomRow key={r.id} room={r} pending={command.pending} onRename={rooms.reload} onDeactivate={() => deactivate(r.id)} />
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

function RoomRow({ room, pending, onRename, onDeactivate }: { room: Schemas["RoomDto"]; pending: boolean; onRename: () => void; onDeactivate: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(room.name);
  const rename = useCommand();

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (await rename.run(() => call(api.PUT("/api/rooms/{id}/name", { params: { path: { id: room.id } }, body: { name } })))) {
      setEditing(false);
      onRename();
    }
  };

  return (
    <tr data-testid={`room-${room.name}`}>
      <td>
        {editing ? (
          <form onSubmit={save} className="sb-actions" style={{ justifyContent: "flex-start" }} noValidate>
            <TextField label={`New name for ${room.name}`} value={name} maxLength={200} error={rename.fieldError("name") ?? rename.error?.message} onChange={(e) => setName(e.target.value)} />
            <div className="sb-form-action">
              <Button type="submit" small variant="primary" disabled={rename.pending}>
                Save name
              </Button>
              <Button small onClick={() => setEditing(false)}>
                Keep old name
              </Button>
            </div>
          </form>
        ) : (
          room.name
        )}
      </td>
      <td>{room.capacity}</td>
      <td>{room.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Deactivated {formatDate(room.deactivatedAt)}</Badge>}</td>
      <td>{formatDate(room.createdAt)}</td>
      <td>
        {room.isActive && !editing && (
          <div className="sb-actions">
            <Button small onClick={() => setEditing(true)} aria-label={`Rename ${room.name}`}>
              Rename
            </Button>
            <ConfirmAction
              label={`Deactivate ${room.name}`}
              confirmLabel="Deactivate for good"
              message={`Deactivate ${room.name}? Its upcoming bookings are cancelled and the bookers are emailed. This cannot be undone.`}
              pending={pending}
              onConfirm={onDeactivate}
            />
          </div>
        )}
      </td>
    </tr>
  );
}
